import { useFeature, WebLink } from '@news-mono/web-common'
import React from 'react'
import { Breakpoints } from '../../__styling/settings/breakpoints'
import {
    breakpoint as caclBreakpoint,
    BreakPointProps,
} from '../../__styling/style-functions'
import {
    StyledImageBanner,
    StyledImageBannerWrapper,
    StyledPicture,
    VerticalGuttersType,
} from './ImageBanner.styled'

interface Source {
    src:
        | string
        | {
              src: string
              density: '2x' | '1x'
          }[]
    breakpoint?: keyof Breakpoints
}

export interface ImageBannerProps {
    verticalGutters?: VerticalGuttersType
    defaultSrc: string
    altText: string
    sources?: Source[]
    url?: string
    forceInternalSSR?: boolean
    hiddenHeading?: string
    disableHeader?: boolean
    // extra styling props passed through here, with a key as a breakpoint, and the object css props, for use on the react component via. {... extraStyles}
    imgStyles?: BreakPointProps
    extraStyles?: BreakPointProps
}

const ImageBannerWrapper: React.FC<Partial<ImageBannerProps>> = ({
    children,
    disableHeader,
}) => {
    if (disableHeader) {
        return <p style={{ margin: 0 }}>{children}</p>
    } else {
        return <h1 style={{ margin: 0 }}>{children}</h1>
    }
}

export const ImageBanner: React.FC<ImageBannerProps> = ({
    defaultSrc,
    altText,
    sources,
    verticalGutters,
    url,
    forceInternalSSR,
    hiddenHeading,
    disableHeader = false,
    imgStyles,
    extraStyles,
}) => {
    const isPerthnowNGN = useFeature('perthnow-ngn-redesign-master')

    const renderImage = (
        <StyledImageBannerWrapper
            isPerthNowNGN={isPerthnowNGN}
            verticalGutters={verticalGutters}
            as={disableHeader ? 'p' : 'h1'}
        >
            <StyledPicture>
                {sources &&
                    sources.map(({ src, breakpoint }, index) => {
                        const srcProps =
                            typeof src === 'string'
                                ? { srcSet: src }
                                : {
                                      srcSet: src
                                          .map(
                                              (src) =>
                                                  `${src.src} ${src.density}`,
                                          )
                                          .join(','),
                                  }

                        return (
                            <source
                                key={index}
                                media={
                                    breakpoint
                                        ? caclBreakpoint(breakpoint, false)
                                        : undefined
                                }
                                {...srcProps}
                            />
                        )
                    })}
                <img src={defaultSrc} alt={hiddenHeading || altText} />
            </StyledPicture>
            <span hidden>{hiddenHeading}</span>
        </StyledImageBannerWrapper>
    )

    return (
        <StyledImageBanner extraStyles={extraStyles} imgStyles={imgStyles}>
            {url ? (
                <WebLink to={url} forceInternalSSR={forceInternalSSR}>
                    {renderImage}
                </WebLink>
            ) : (
                renderImage
            )}
        </StyledImageBanner>
    )
}
ImageBanner.displayName = 'ImageBanner'
